import { createJurisdictionHeaders } from '@/data';
import { URLS } from '@/data/config';
import { queryClient } from '@/data/reactQueryClient';
import { useTrustlyInfoOverrideStore } from '@/feature/devsettings/hooks/use-trustly-info-override-store';
import { useQuery } from '@tanstack/react-query';

export enum LightSdd {
    YES = 'YES',
    NO_REQUIRED = 'NO_REQUIRED',
    NO = 'NO',
    IGNORE = 'IGNORE',
}

type AuthUserTrustlyInfoResponse = {
    TRUSTLY_KYC_SUCCESS?: boolean;
    TRUSTLY_VERIFIED?: boolean;
    RESTRICTED_BALANCE_AMOUNT?: number;
    HAS_RESTRICTED_BALANCE?: boolean;
    LIGHT_SDD?: string; // For backward compatibility we don't set enum.
    IS_IDPV?: boolean;
};

export const trustlyInfoKeys = {
    all: ['auth-user-trustly-info'],
};

const getAuthUserTrustlyInfo = async (): Promise<AuthUserTrustlyInfoResponse> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/user/trustly-info`, {
        method: 'GET',
        headers: createJurisdictionHeaders(),
    });

    const data = await response.json();
    if (!response.ok) {
        throw data;
    }
    return data.data;
};

export const useGetAuthUserTrustlyInfo = () => {
    // Dev-only override set from the Developer screen so QA can simulate any
    // withdraw verification gate state without back-office setup.
    const devOverride = useTrustlyInfoOverrideStore(state => (state.enabled ? state.values : undefined));
    const { data, ...rest } = useQuery({
        queryKey: trustlyInfoKeys.all,
        queryFn: getAuthUserTrustlyInfo,
        gcTime: 0,
        staleTime: 0,
    });

    return { ...rest, data: devOverride ? { ...data, ...devOverride } : data };
};

/**
 * One-shot fetch of the trustly info through the query cache, with the dev override
 * applied — for call-time decisions outside React rendering (e.g. the IDPV failure
 * modal). Resolves undefined when the fetch fails and no override is active.
 */
export const fetchAuthUserTrustlyInfo = async (): Promise<AuthUserTrustlyInfoResponse | undefined> => {
    const { enabled: overrideEnabled, values: overrideValues } = useTrustlyInfoOverrideStore.getState();
    try {
        const data = await queryClient.fetchQuery({
            queryKey: trustlyInfoKeys.all,
            queryFn: getAuthUserTrustlyInfo,
        });
        return overrideEnabled ? { ...data, ...overrideValues } : data;
    } catch {
        return overrideEnabled ? overrideValues : undefined;
    }
};
